/**
 * El pedido que arma el cliente en la página, y el mensaje que llega al
 * WhatsApp de la tienda.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ WHATSAPP Y NO UN PAGO EN LÍNEA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque así venden estas tiendas: el cliente escribe, se pone de acuerdo con
 * el dueño en el pago y la entrega, y listo. Un carrito que cobra en línea
 * pediría una pasarela —que en Softfi está apagada hasta probarla bien— y
 * dejaría fuera el Pago Móvil y el «te lo paso cuando llegue».
 *
 * Lo que sí hace la página es ahorrarle el trabajo a los dos: el cliente no
 * escribe «hola, ¿cuánto cuesta el bolígrafo rosado?» cinco veces, y el dueño
 * recibe el pedido entero, con cantidades y total, en un solo mensaje.
 */

import type { DatoDelCliente, EntregaDelPedido, IngredienteQuitado, ModalidadDeEntrega } from "./tipos";
import { textoDeSin } from "@/lib/funcionesDelRestaurante";
import { formatMoney, redondearMoneda, toNumber } from "@/lib/money";
import type { CajaPublica, MonedaPublica } from "./tipos";

export interface LineaDePedido {
  /**
   * Producto + variante + lo quitado: la misma blusa en S y en M son dos
   * líneas, y la hamburguesa sin tomate no se junta con la completa.
   */
  clave: string;
  productoId: string;
  varianteId: string | null;
  /** Si es una caja de venta («Caja ×12») y no unidades sueltas. */
  cajaId?: string | null;
  nombre: string;
  /** «Talla M», «M / Negro», «Caja ×12». */
  detalle: string | null;
  /** En la moneda principal, como lo manda el servidor. */
  precio: string;
  cantidad: number;
  /** Lo que el cliente pidió quitar: «Sin Tomate» (2026-11-03). No cambia el precio. */
  sin?: IngredienteQuitado[];
}

/*
  Sin notas desde el 2026-09-24, por decisión del dueño: «si al final se
  concreta por WhatsApp», el detalle se habla en el chat.

  ⚠️ La barra del final se queda: es donde iba la nota, y así la clave de una
  línea guardada SIN nota sigue siendo la misma y se junta con la nueva.
*/
export function claveDeLinea(
  productoId: string,
  varianteId: string | null,
  cajaId: string | null = null,
  sin: readonly IngredienteQuitado[] = [],
): string {
  // La caja va donde iría la talla: un producto con cajas no tiene tallas.
  if (cajaId) return `${productoId}|caja:${cajaId}|`;
  // Lo quitado va tras la barra: sin nada quitado, la clave es la de siempre.
  const quitado = sin.map((q) => q.variant_id).sort().join(",");
  return `${productoId}|${varianteId ?? ""}|${quitado}`;
}

/** «Caja ×12»: como sale en el pedido y en el recibo del mostrador. */
export function nombreDeCaja(caja: CajaPublica): string {
  return `${caja.nombre} ×${caja.unidades.replace(".", ",")}`;
}

/** Suma una línea al pedido, o le sube la cantidad si ya estaba. */
export function agregarLinea(lineas: LineaDePedido[], nueva: LineaDePedido): LineaDePedido[] {
  const existe = lineas.find((l) => l.clave === nueva.clave);
  if (!existe) return [...lineas, nueva];
  return lineas.map((l) =>
    l.clave === nueva.clave ? { ...l, cantidad: Math.min(99, l.cantidad + nueva.cantidad) } : l,
  );
}

export function cambiarCantidad(lineas: LineaDePedido[], clave: string, cantidad: number): LineaDePedido[] {
  if (cantidad <= 0) return lineas.filter((l) => l.clave !== clave);
  return lineas.map((l) => (l.clave === clave ? { ...l, cantidad: Math.min(99, cantidad) } : l));
}

export function unidades(lineas: LineaDePedido[]): number {
  return lineas.reduce((s, l) => s + l.cantidad, 0);
}

/** El total en la moneda principal. */
export function totalPrincipal(lineas: LineaDePedido[]): number {
  return lineas.reduce((s, l) => s + toNumber(l.precio) * l.cantidad, 0);
}

/**
 * Un monto de la moneda principal, dicho en la moneda que eligió el cliente.
 *
 * ⚠️ Con el MISMO redondeo que la caja (`redondearMoneda`). Si los bolívares se
 * cobran de uno en uno, la página dice 482 Bs y no 481,37: lo que el cliente ve
 * en la página es lo que le van a pedir en el mostrador.
 */
/**
 * Lo que se dice cuando el precio es cero.
 *
 * ⚠️ Cero en Softfi es «se cobra lo que toque» —una recarga, un servicio que
 * se cotiza—, no «gratis». «$0,00» en una vitrina asusta o confunde.
 */
export const PRECIO_A_CONSULTAR = "Precio a consultar";

/** Un importe, o «Precio a consultar» si es cero. */
export function importe(montoPrincipal: string | number, moneda: MonedaPublica): string {
  return toNumber(montoPrincipal) > 0 ? enMoneda(montoPrincipal, moneda) : PRECIO_A_CONSULTAR;
}

export function enMoneda(montoPrincipal: string | number, moneda: MonedaPublica): string {
  return formatMoney(montoEnMoneda(montoPrincipal, moneda), moneda);
}

/** El mismo monto, en número: lo que compara el filtro de precio con lo que se ve. */
export function montoEnMoneda(montoPrincipal: string | number, moneda: MonedaPublica): number {
  const monto = toNumber(montoPrincipal);
  if (moneda.is_primary) return monto;
  return redondearMoneda(monto * toNumber(moneda.current_rate), moneda);
}

/**
 * Los datos que la tienda le puede pedir al cliente (2026-10-07), en el orden
 * del formulario: con qué se rotulan, cómo se dicen en el WhatsApp y cómo los
 * llena el navegador solo.
 */
export const DATOS_DEL_CLIENTE: Record<
  DatoDelCliente,
  { etiqueta: string; enMensaje: string; ayuda: string; autocompletar: string; tipo: string; largo: number }
> = {
  nombre: {
    etiqueta: "Tu nombre",
    enMensaje: "Mi nombre",
    ayuda: "Para que sepan quién pide",
    autocompletar: "name",
    tipo: "text",
    largo: 60,
  },
  telefono: {
    etiqueta: "Tu teléfono",
    enMensaje: "Mi teléfono",
    ayuda: "Para avisarte del pedido",
    autocompletar: "tel",
    tipo: "tel",
    largo: 30,
  },
  documento: {
    etiqueta: "Tu cédula o documento",
    enMensaje: "Mi cédula o documento",
    ayuda: "Para la nota de entrega",
    autocompletar: "off",
    tipo: "text",
    largo: 30,
  },
  correo: {
    etiqueta: "Tu correo",
    enMensaje: "Mi correo",
    ayuda: "nombre@correo.com",
    autocompletar: "email",
    tipo: "email",
    largo: 120,
  },
  direccion: {
    etiqueta: "Dirección de entrega",
    enMensaje: "Dirección de entrega",
    ayuda: "Calle, casa y un punto de referencia",
    autocompletar: "street-address",
    tipo: "text",
    largo: 300,
  },
};

export const ORDEN_DE_DATOS: DatoDelCliente[] = ["nombre", "telefono", "documento", "correo", "direccion"];

/** Lo que pide la tienda si no dice nada: el nombre, opcional (lo de siempre). */
export const PIDE_DE_FABRICA = { pedir: ["nombre"] as DatoDelCliente[], obligatorios: [] as DatoDelCliente[] };

/** Los obligatorios que faltan por llenar, en el orden del formulario. */
export function faltanDatos(
  pide: { obligatorios: DatoDelCliente[] },
  valores: Partial<Record<DatoDelCliente, string>>,
): DatoDelCliente[] {
  return ORDEN_DE_DATOS.filter((c) => pide.obligatorios.includes(c) && !(valores[c] ?? "").trim());
}

// =============================================================================
//  Retiro o delivery (2026-11-02)
// =============================================================================

/**
 * Lo pidió el dueño para los restaurantes: «si es para pickup, para
 * delivery… si es para delivery pedir más datos… que envíe el GPS con un
 * click». Con los MISMOS nombres que la nota de la cotización
 * (`pedidos.py::MODALIDADES`).
 */
export const MODALIDADES: Record<ModalidadDeEntrega, string> = {
  retiro: "Retiro en el local",
  delivery: "Delivery",
};

/** Dónde está el cliente, según su teléfono. `precision` en metros. */
export interface UbicacionDelCliente {
  lat: number;
  lng: number;
  precision: number | null;
}

/** Lo que eligió y escribió el cliente sobre la entrega. */
export interface EntregaElegida {
  modalidad: ModalidadDeEntrega | null;
  direccion: string;
  referencia: string;
  ubicacion: UbicacionDelCliente | null;
}

export const SIN_ENTREGA_ELEGIDA: EntregaElegida = { modalidad: null, direccion: "", referencia: "", ubicacion: null };

/** Lo que falta de la entrega, en el orden del formulario. */
export type FaltaDeEntrega = "modalidad" | "direccion" | "ubicacion";

export const NOMBRE_DE_LO_QUE_FALTA: Record<FaltaDeEntrega, string> = {
  modalidad: "forma de entrega",
  direccion: "dirección de entrega",
  ubicacion: "ubicación",
};

/** La que queda elegida si la tienda ofrece una sola: con una, ni se pregunta. */
export function modalidadInicial(entrega: EntregaDelPedido | undefined): ModalidadDeEntrega | null {
  return entrega?.modalidades.length === 1 ? entrega.modalidades[0]! : null;
}

export function faltaEnLaEntrega(entrega: EntregaDelPedido | undefined, elegida: EntregaElegida): FaltaDeEntrega[] {
  if (!entrega?.modalidades.length) return [];
  if (!elegida.modalidad || !entrega.modalidades.includes(elegida.modalidad)) return ["modalidad"];
  if (elegida.modalidad !== "delivery") return [];
  const faltan: FaltaDeEntrega[] = [];
  if (!elegida.direccion.trim()) faltan.push("direccion");
  if (entrega.ubicacion === "obligatoria" && !elegida.ubicacion) faltan.push("ubicacion");
  return faltan;
}

/**
 * El enlace que abre la ubicación en el mapa. ⚠️ El MISMO que guarda el
 * servidor en la nota (`pedidos.py::enlace_del_mapa`).
 */
export function enlaceDelMapa(lat: number, lng: number): string {
  return `https://www.google.com/maps?q=${lat.toFixed(6)},${lng.toFixed(6)}`;
}

/**
 * «±1,2 km» si el teléfono no dio la ubicación precisa; `null` si sí.
 *
 * Sin GPS, el navegador ubica por la red y puede errar por kilómetros: el
 * repartidor tiene que saber que ese punto es solo la zona.
 */
export function margenDeUbicacion(metros: number | null): string | null {
  if (metros === null || metros <= 150) return null;
  if (metros < 1000) return `±${Math.round(metros)} m`;
  return `±${(metros / 1000).toFixed(1).replace(".", ",")} km`;
}

/** « (aproximada, ±1,2 km)», o nada. Igual que `pedidos.py::_aproximada`. */
export function aproximada(metros: number | null): string {
  const margen = margenDeUbicacion(metros);
  return margen ? ` (aproximada, ${margen})` : "";
}

/** Por qué el teléfono no dio la ubicación, dicho para el cliente. */
export function mensajeDeUbicacion(codigo: number | "sin_soporte"): string {
  if (codigo === "sin_soporte") {
    return "Este navegador no permite compartir la ubicación. Indica un punto de referencia en la dirección.";
  }
  if (codigo === 1) {
    return "No se permitió el acceso a la ubicación. Puedes activarlo en los permisos del navegador o indicar un punto de referencia.";
  }
  if (codigo === 3) return "La ubicación tardó demasiado en responder. Intenta de nuevo.";
  return "No se pudo obtener la ubicación. Verifica que el GPS del teléfono esté encendido e intenta de nuevo.";
}

export interface DatosDelMensaje {
  tienda: string;
  lineas: LineaDePedido[];
  moneda: MonedaPublica;
  /** «catálogo» o «menú». */
  deDonde: string;
  nombre?: string;
  /** Los demás datos del cliente que pidió la tienda (2026-10-07). */
  cliente?: Partial<Record<DatoDelCliente, string>>;
  /** «COT-000123»: con qué número quedó anotado en Softfi. Vacío si no se pudo. */
  numero?: string;
  /** Retiro o delivery, si la tienda lo pregunta (2026-11-02). */
  entrega?: EntregaElegida;
}

/**
 * El mensaje que se abre en WhatsApp, listo para enviar.
 *
 *     Hola, *Moondo* 👋
 *     Quiero hacer el pedido *COT-000123* desde tu catálogo:
 *
 *     • 2 × Bolígrafo rosado — $6,40
 *     • 1 × Blusa manga larga (Talla M) — $20,00
 *     • 1 × Hamburguesa — $6,00
 *        *Sin Tomate · Sin Cebolla*
 *
 *     *Total: $26,40*
 *
 * Los asteriscos son las negritas de WhatsApp: el total se ve de un vistazo en
 * la conversación, que es lo primero que busca el dueño.
 *
 * El número es el de la cotización que ya quedó en Softfi: con él, el dueño
 * la abre en «Cotizaciones» y la cobra sin volver a escribir el pedido. Si no
 * se pudo anotar (sin conexión, la tienda recibió demasiados), el mensaje sale
 * igual, sin número: el pedido por WhatsApp no puede depender de eso.
 */
export function mensajeDePedido(d: DatosDelMensaje): string {
  const renglones = d.lineas.map((l) => {
    const detalle = l.detalle ? ` (${l.detalle})` : "";
    const subtotal = toNumber(l.precio) * l.cantidad;
    // En minúscula solo la frase: «117,00 Bs» tiene que seguir diciendo «Bs».
    const monto = subtotal > 0 ? enMoneda(subtotal, d.moneda) : "precio a consultar";
    // En negrita y en su renglón: es lo que, si se pasa por alto, arruina el plato.
    const quitado = l.sin?.length ? `\n   *${textoDeSin(l.sin)}*` : "";
    return `• ${l.cantidad} × ${l.nombre}${detalle} — ${monto}${quitado}`;
  });

  const total = totalPrincipal(d.lineas);
  const hayAConsultar = d.lineas.some((l) => toNumber(l.precio) <= 0);

  const partes = [
    `Hola, *${d.tienda}* 👋`,
    d.numero
      ? `Quiero hacer el pedido *${d.numero}* desde tu ${d.deDonde}:`
      : `Quiero hacer este pedido desde tu ${d.deDonde}:`,
    "",
    ...renglones,
    "",
  ];
  if (total > 0) {
    partes.push(`*Total: ${enMoneda(total, d.moneda)}*${hayAConsultar ? " + lo que está a consultar" : ""}`);
  } else {
    partes.push("*Total: a consultar*");
  }

  // Si eligió otra moneda, que el dueño sepa a qué tasa lo vio el cliente: si
  // la cambia antes de contestar, la diferencia no es un malentendido.
  if (!d.moneda.is_primary) {
    partes.push(`_(precios en ${d.moneda.code} a la tasa de hoy)_`);
  }

  // Lo que dio el cliente, debajo del total: el nombre y lo demás que pide la tienda.
  const datos = { ...d.cliente, ...(d.nombre !== undefined ? { nombre: d.nombre } : {}) };
  const lineasDelCliente = ORDEN_DE_DATOS.filter((c) => (datos[c] ?? "").trim()).map(
    (c) => `${DATOS_DEL_CLIENTE[c].enMensaje}: ${datos[c]!.trim()}`,
  );
  if (lineasDelCliente.length) partes.push("", ...lineasDelCliente);

  // La entrega al final, en su bloque: es lo que el dueño le reenvía al
  // repartidor, y así lo copia de una vez.
  const entrega = d.entrega;
  if (entrega?.modalidad) {
    partes.push("", `*Entrega: ${MODALIDADES[entrega.modalidad]}*`);
    if (entrega.modalidad === "delivery") {
      if (entrega.direccion.trim()) partes.push(`Dirección de entrega: ${entrega.direccion.trim()}`);
      if (entrega.referencia.trim()) partes.push(`Punto de referencia: ${entrega.referencia.trim()}`);
      if (entrega.ubicacion) {
        const { lat, lng, precision } = entrega.ubicacion;
        partes.push(`📍 Mi ubicación: ${enlaceDelMapa(lat, lng)}${aproximada(precision)}`);
      }
    }
  }

  return partes.join("\n");
}

// =============================================================================
//  El pedido también entra a Softfi, como cotización
// =============================================================================

export interface PedidoParaSoftfi {
  lineas: {
    producto_id: string;
    variante_id: string | null;
    /** Solo si es una caja de venta. */
    caja_id?: string;
    /** Los ingredientes quitados: solo sus `variant_id`. */
    sin?: string[];
    cantidad: number;
  }[];
  nombre: string | null;
  telefono?: string | null;
  documento?: string | null;
  correo?: string | null;
  direccion?: string | null;
  /** La entrega (2026-11-02): solo si la tienda la pregunta. */
  modalidad?: ModalidadDeEntrega;
  referencia?: string;
  ubicacion?: UbicacionDelCliente;
  /** El campo que la página no llena y un robot sí. Ver `pedidos.py`. */
  trampa: string | null;
}

/**
 * Lo que se le manda a Softfi: qué producto y cuántos. ⚠️ SIN PRECIOS: el
 * precio lo pone el servidor, igual que en la caja.
 */
export function cuerpoDelPedido(
  lineas: LineaDePedido[],
  nombre: string,
  trampa = "",
  otros: Partial<Record<Exclude<DatoDelCliente, "nombre">, string>> = {},
  entrega?: EntregaElegida,
): PedidoParaSoftfi {
  // Solo los que tienen algo: lo vacío no viaja.
  const extra = Object.fromEntries(
    Object.entries(otros)
      .map(([c, v]) => [c, (v ?? "").trim()])
      .filter(([, v]) => v),
  );
  // La dirección del delivery va en el mismo campo que la de los datos del
  // cliente: con la entrega encendida, la página no pide esa.
  const delivery = entrega?.modalidad === "delivery" ? entrega : null;
  const deLaEntrega = {
    ...(entrega?.modalidad ? { modalidad: entrega.modalidad } : {}),
    ...(delivery?.direccion.trim() ? { direccion: delivery.direccion.trim() } : {}),
    ...(delivery?.referencia.trim() ? { referencia: delivery.referencia.trim() } : {}),
    ...(delivery?.ubicacion ? { ubicacion: delivery.ubicacion } : {}),
  };
  return {
    ...extra,
    ...deLaEntrega,
    lineas: lineas.map((l) => ({
      producto_id: l.productoId,
      variante_id: l.varianteId,
      ...(l.cajaId ? { caja_id: l.cajaId } : {}),
      ...(l.sin?.length ? { sin: l.sin.map((q) => q.variant_id) } : {}),
      cantidad: l.cantidad,
    })),
    nombre: nombre.trim() || null,
    trampa: trampa || null,
  };
}

/** Cuánto se espera a Softfi antes de abrir WhatsApp sin número. */
export const ESPERA_MAXIMA_MS = 5000;

/**
 * Anota el pedido en Softfi y devuelve su número, o "" si no se pudo.
 *
 * ⚠️ Nunca lanza un error. Sin conexión, con la tienda llena de pedidos o con
 * el servidor lento, el cliente igual tiene que poder mandar su pedido por
 * WhatsApp: eso funcionaba antes de que existiera esto.
 */
export async function registrarPedido(
  tienda: string,
  cuerpo: PedidoParaSoftfi,
  esperaMs = ESPERA_MAXIMA_MS,
): Promise<string> {
  const control = new AbortController();
  const reloj = window.setTimeout(() => control.abort(), esperaMs);
  try {
    const r = await fetch(`/api/v1/publico/catalogos/${encodeURIComponent(tienda)}/pedidos`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(cuerpo),
      signal: control.signal,
    });
    if (!r.ok) return "";
    const datos = (await r.json()) as { numero?: string };
    return datos.numero ?? "";
  } catch {
    return "";
  } finally {
    window.clearTimeout(reloj);
  }
}
